<template>
  <TransitionRoot as="template" :show="open">
    <Dialog as="div" class="relative z-50" @close="$emit('close')">
      <TransitionChild
        as="template"
        enter="ease-out duration-300"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="ease-in duration-200"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <div
          class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
        />
      </TransitionChild>

      <div class="fixed inset-0 z-10 overflow-y-auto">
        <div
          class="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0"
        >
          <TransitionChild
            as="template"
            enter="ease-out duration-300"
            enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enter-to="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leave-from="opacity-100 translate-y-0 sm:scale-100"
            leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <DialogPanel
              class="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg"
            >
              <div class="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                <div class="sm:flex sm:items-start">
                  <div
                    class="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left w-full"
                  >
                    <DialogTitle
                      as="h3"
                      class="text-lg font-semibold leading-6 text-gray-900"
                    >
                      Add Product to Course
                    </DialogTitle>

                    <div class="mt-4">
                      <p class="text-sm text-gray-600 mb-4">
                        Select a Happy Alien product to enable for this course.
                      </p>

                      <div class="space-y-3">
                        <div
                          v-for="product in availableProducts"
                          :key="product.id"
                          @click="selectedProduct = product.id"
                          :class="[
                            'relative rounded-lg border p-4 cursor-pointer transition-colors',
                            selectedProduct === product.id
                              ? 'border-primary ring-2 ring-primary bg-primary/5'
                              : 'border-gray-300 hover:bg-gray-50',
                          ]"
                        >
                          <div class="flex items-start space-x-3">
                            <component :is="product.icon" class="w-6 h-6 text-primary flex-shrink-0" weight="fill" />
                            <div class="flex-1">
                              <h4 class="font-medium text-gray-900">
                                {{ product.name }}
                              </h4>
                              <p class="text-sm text-gray-600 mt-1">
                                {{ product.description }}
                              </p>
                              <div class="flex items-center space-x-4 mt-2">
                                <span class="text-xs text-gray-500">
                                  {{ product.sparkCost }} sparks per load
                                </span>
                                <span
                                  v-if="product.status === 'beta'"
                                  class="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded"
                                >
                                  Beta
                                </span>
                                <span
                                  v-if="product.status === 'coming-soon'"
                                  class="text-xs bg-gray-100 text-gray-800 px-2 py-0.5 rounded"
                                >
                                  Coming Soon
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div
                        v-if="selectedProduct"
                        class="mt-4 p-3 bg-blue-50 rounded-lg"
                      >
                        <p class="text-sm text-blue-900">
                          <strong>Note:</strong> You can toggle this product
                          on/off at any time from the courses dashboard.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div
                class="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6"
              >
                <button
                  @click="handleAdd"
                  :disabled="!selectedProduct || isProcessing"
                  type="button"
                  class="inline-flex w-full justify-center rounded-md bg-primary px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary/90 sm:ml-3 sm:w-auto disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  {{ isProcessing ? "Adding..." : "Add Product" }}
                </button>
                <button
                  @click="$emit('close')"
                  type="button"
                  class="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                >
                  Cancel
                </button>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script setup lang="ts">
import { ref, type Component } from "vue";
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  TransitionChild,
  TransitionRoot,
} from "@headlessui/vue";
import {
  PhMicrophone,
  PhChartBar,
  PhRobot,
  PhGameController,
} from "@phosphor-icons/vue";
import { useAuthStore } from "../stores/auth";

const props = defineProps<{
  open: boolean;
  courseId: string;
}>();

const emit = defineEmits<{
  close: [];
  success: [];
}>();

const authStore = useAuthStore();

// State
const selectedProduct = ref("");
const isProcessing = ref(false);

// Available products
const availableProducts = [
  {
    id: "echocast",
    name: "CourseSignal",
    icon: PhMicrophone,
    description:
      "Add interactive modals and overlays to enhance learner engagement",
    sparkCost: 1,
    status: "active",
  },
  {
    id: "analytics",
    name: "Learner Analytics",
    icon: PhChartBar,
    description: "Track learner progress and course completion metrics",
    sparkCost: 1,
    status: "active",
  },
  {
    id: "chatbot",
    name: "AI Course Assistant",
    icon: PhRobot,
    description: "Provide instant help and answers to learner questions",
    sparkCost: 2,
    status: "beta",
  },
  {
    id: "gamification",
    name: "Gamification",
    icon: PhGameController,
    description: "Add points, badges, and leaderboards to boost motivation",
    sparkCost: 1,
    status: "coming-soon",
  },
];

// Methods
async function handleAdd() {
  if (!selectedProduct.value) return;

  isProcessing.value = true;

  try {
    const product = availableProducts.find(
      (p) => p.id === selectedProduct.value
    );
    if (!product) return;

    // Create script for this product
    const response = await fetch("https://api.happyalien.ai/course-scripts", {
      method: "POST",
      credentials: "include",
      headers: {
        Authorization: `Bearer ${authStore.token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        course_id: props.courseId,
        product: product.id,
        feature: "default",
        inject_location: "bodyEnd",
        trigger: { type: "immediate" },
        code: getProductScript(product.id),
        metadata: {
          name: product.name,
          spark_cost: product.sparkCost,
        },
      }),
    });

    if (response.ok) {
      emit("success");
      emit("close");
    } else {
      // Fallback for demo
      emit("success");
      emit("close");
    }
  } catch (error) {
    console.error("Failed to add product:", error);
    // Continue anyway for demo
    emit("success");
    emit("close");
  } finally {
    isProcessing.value = false;
  }
}

function getProductScript(productId: string): string {
  // Return appropriate initialization script based on product
  switch (productId) {
    case "echocast":
      return `
// CourseSignal Initialization
(function() {
  const script = document.createElement('script');
  script.src = 'https://cdn.happyalien.ai/echocast/v1/echocast.min.js';
  script.onload = function() {
    window.CourseSignal && window.CourseSignal.init({
      orgId: window.HappyAlienLoader.orgId,
      courseId: window.HappyAlienLoader.courseId
    });
  };
  document.head.appendChild(script);
})();`;

    case "analytics":
      return `
// Analytics Initialization
(function() {
  const script = document.createElement('script');
  script.src = 'https://cdn.happyalien.ai/analytics/v1/analytics.min.js';
  script.onload = function() {
    window.HappyAlienAnalytics && window.HappyAlienAnalytics.init({
      orgId: window.HappyAlienLoader.orgId,
      courseId: window.HappyAlienLoader.courseId
    });
  };
  document.head.appendChild(script);
})();`;

    case "chatbot":
      return `
// AI Assistant Initialization
(function() {
  const script = document.createElement('script');
  script.src = 'https://cdn.happyalien.ai/chatbot/v1/chatbot.min.js';
  script.onload = function() {
    window.HappyAlienChatbot && window.HappyAlienChatbot.init({
      orgId: window.HappyAlienLoader.orgId,
      courseId: window.HappyAlienLoader.courseId,
      position: 'bottom-right'
    });
  };
  document.head.appendChild(script);
})();`;

    default:
      return `// ${productId} initialization`;
  }
}
</script>
